import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  ClipboardList,
  Mail,
  MapPin,
  PackagePlus,
  RefreshCw,
  Search,
  ShoppingCart,
  Trash2,
  UserPlus,
} from "lucide-react";
import { api } from "../../services/api";
import { InsightCard, PaginationControls, StatusPill } from "./AdminPanelParts";
import { usePagination } from "./usePagination";
import { ToastStack, useToast } from "./useToast";
import { formatMoney, normalizeOrder, paymentTone, statusTone } from "./orderUtils";

const emptyCustomer = {
  customerName: "",
  customerEmail: "",
  phone: "",
};

const emptyAddress = {
  fullName: "",
  phone: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "United States",
};

const emptySettings = {
  channel: "direct",
  status: "processing",
  paymentStatus: "paid",
  paymentProvider: "manual",
  shipping: 0,
  tax: 0,
  adminNotes: "",
};

const emptyManualItem = {
  name: "",
  sku: "",
  quantity: 1,
  price: "",
};

function lineTotal(item) {
  return Number(item.quantity || 1) * Number(item.price || 0);
}

function customerDiscount(customer) {
  return Number(customer?.customerDiscount?.percent || 0);
}

function firstAddress(customer) {
  const addresses = customer?.addresses || [];
  return addresses.find((address) => address.isDefault) || addresses[0] || null;
}

function itemFromProduct(product) {
  return {
    key: `${product._id || product.sku}-${Date.now()}`,
    productId: product._id,
    name: product.name || "",
    sku: product.sku || "",
    quantity: 1,
    price: Number(product.price || 0),
    image: product.image || "",
    stock: Number(product.stock || 0),
    source: "catalog",
  };
}

function cleanOrderItem(item) {
  return {
    productId: item.productId,
    name: item.name.trim(),
    sku: item.sku.trim(),
    quantity: Number(item.quantity || 1),
    price: Number(item.price || 0),
    image: item.image || "",
  };
}

function searchableOrder(order) {
  return [
    order.orderNumber,
    order.user?.name,
    order.user?.email,
    order.channel,
    order.status,
    order.paymentStatus,
    order.items?.map((item) => `${item.name} ${item.sku}`).join(" "),
  ]
    .join(" ")
    .toLowerCase();
}

export default function Customorder() {
  const navigate = useNavigate();
  const { dismiss, toast, toasts } = useToast();
  const [customerSearch, setCustomerSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [orderSearch, setOrderSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerForm, setCustomerForm] = useState(emptyCustomer);
  const [address, setAddress] = useState(emptyAddress);
  const [settings, setSettings] = useState(emptySettings);
  const [items, setItems] = useState([]);
  const [manualItem, setManualItem] = useState(emptyManualItem);
  const [loading, setLoading] = useState({ customers: false, products: false, orders: false, saving: false });

  const loadCustomers = useCallback(async (search = "") => {
    setLoading((current) => ({ ...current, customers: true }));
    try {
      const data = await api.get(`/customers?search=${encodeURIComponent(search)}&limit=12`);
      setCustomers(data.customers || []);
    } catch (err) {
      toast(`Could not load customers: ${err.message}`, "error");
    } finally {
      setLoading((current) => ({ ...current, customers: false }));
    }
  }, [toast]);

  const loadProducts = useCallback(async (search = "") => {
    setLoading((current) => ({ ...current, products: true }));
    try {
      const data = await api.get(`/products?search=${encodeURIComponent(search)}&limit=12`);
      setProducts(data.products || []);
    } catch (err) {
      toast(`Could not load products: ${err.message}`, "error");
    } finally {
      setLoading((current) => ({ ...current, products: false }));
    }
  }, [toast]);

  const loadOrders = useCallback(async () => {
    setLoading((current) => ({ ...current, orders: true }));
    try {
      const data = await api.get("/orders");
      setOrders((data.orders || []).map(normalizeOrder));
    } catch (err) {
      toast(`Could not load recent orders: ${err.message}`, "error");
    } finally {
      setLoading((current) => ({ ...current, orders: false }));
    }
  }, [toast]);

  useEffect(() => {
    loadCustomers("");
    loadProducts("");
    loadOrders();
  }, [loadCustomers, loadOrders, loadProducts]);

  useEffect(() => {
    const timer = window.setTimeout(() => loadCustomers(customerSearch), 250);
    return () => window.clearTimeout(timer);
  }, [customerSearch, loadCustomers]);

  useEffect(() => {
    const timer = window.setTimeout(() => loadProducts(productSearch), 250);
    return () => window.clearTimeout(timer);
  }, [productSearch, loadProducts]);

  const visibleOrders = useMemo(() => {
    const term = orderSearch.trim().toLowerCase();
    if (!term) return orders;
    return orders.filter((order) => searchableOrder(order).includes(term));
  }, [orders, orderSearch]);

  const orderPagination = usePagination(visibleOrders, [orderSearch], 8);

  const totals = useMemo(() => {
    const subtotal = items.reduce((sum, item) => sum + lineTotal(item), 0);
    const discountPercent = customerDiscount(selectedCustomer);
    const discount = Number(((subtotal * discountPercent) / 100).toFixed(2));
    const taxableSubtotal = Math.max(0, subtotal - discount);
    const tax = Number(settings.tax || 0);
    const shipping = Number(settings.shipping || 0);
    const total = Number((taxableSubtotal + shipping + tax).toFixed(2));
    return { discount, discountPercent, shipping, subtotal, tax, total };
  }, [items, selectedCustomer, settings.shipping, settings.tax]);

  const metrics = useMemo(() => {
    return {
      draftItems: items.length,
      draftTotal: totals.total,
      openManualOrders: visibleOrders.filter((order) => ["pending", "processing", "paid"].includes(order.status)).length,
      unpaid: visibleOrders.filter((order) => order.paymentStatus !== "paid").length,
    };
  }, [items.length, totals.total, visibleOrders]);

  const selectCustomer = (customer) => {
    const selectedAddress = firstAddress(customer);
    setSelectedCustomer(customer);
    setCustomerForm({
      customerName: customer.name || "",
      customerEmail: customer.email || "",
      phone: customer.phone || "",
    });
    setAddress({
      ...emptyAddress,
      ...(selectedAddress || {}),
      fullName: selectedAddress?.fullName || customer.name || "",
      phone: selectedAddress?.phone || customer.phone || "",
    });
    toast(`Selected ${customer.name}`, "success");
  };

  const updateCustomerForm = (event) => {
    const { name, value } = event.target;
    setCustomerForm((current) => ({ ...current, [name]: value }));
    if (selectedCustomer && ["customerEmail", "customerName"].includes(name)) {
      setSelectedCustomer(null);
    }
  };

  const updateAddress = (event) => {
    const { name, value } = event.target;
    setAddress((current) => ({ ...current, [name]: value }));
  };

  const updateSettings = (event) => {
    const { name, value } = event.target;
    setSettings((current) => ({ ...current, [name]: value }));
  };

  const updateManualItem = (event) => {
    const { name, value } = event.target;
    setManualItem((current) => ({ ...current, [name]: value }));
  };

  const addProduct = (product) => {
    setItems((current) => {
      const existingIndex = current.findIndex((item) => item.productId && item.productId === product._id);
      if (existingIndex >= 0) {
        return current.map((item, index) =>
          index === existingIndex ? { ...item, quantity: Number(item.quantity || 1) + 1 } : item
        );
      }
      return [...current, itemFromProduct(product)];
    });
  };

  const addManualItem = (event) => {
    event.preventDefault();
    if (!manualItem.name.trim()) {
      toast("Manual item name is required", "error");
      return;
    }
    setItems((current) => [
      ...current,
      {
        key: `manual-${Date.now()}`,
        name: manualItem.name,
        sku: manualItem.sku,
        quantity: Number(manualItem.quantity || 1),
        price: Number(manualItem.price || 0),
        source: "manual",
      },
    ]);
    setManualItem(emptyManualItem);
  };

  const updateItem = (index, field, value) => {
    setItems((current) => current.map((item, itemIndex) =>
      itemIndex === index ? { ...item, [field]: value } : item
    ));
  };

  const removeItem = (index) => {
    setItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const resetDraft = () => {
    setSelectedCustomer(null);
    setCustomerForm(emptyCustomer);
    setAddress(emptyAddress);
    setSettings(emptySettings);
    setItems([]);
    setManualItem(emptyManualItem);
  };

  const createOrder = async (event) => {
    event.preventDefault();
    if (!items.length) {
      toast("Add at least one item before placing the order", "error");
      return;
    }

    setLoading((current) => ({ ...current, saving: true }));
    try {
      const payload = {
        customerName: customerForm.customerName,
        customerEmail: customerForm.customerEmail,
        channel: settings.channel,
        status: settings.status,
        paymentStatus: settings.paymentStatus,
        paymentProvider: settings.paymentProvider,
        shipping: Number(settings.shipping || 0),
        tax: Number(settings.tax || 0),
        adminNotes: settings.adminNotes,
        items: items.map(cleanOrderItem),
        shippingAddress: {
          ...address,
          fullName: address.fullName || customerForm.customerName,
          phone: address.phone || customerForm.phone,
        },
      };
      const data = await api.post("/orders", payload);
      const created = normalizeOrder(data.order);
      setOrders((current) => [created, ...current]);
      toast(`Created ${created.orderNumber}`, "success");
      resetDraft();
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setLoading((current) => ({ ...current, saving: false }));
    }
  };

  return (
    <div className="p-6 md:p-10">
      <ToastStack dismiss={dismiss} toasts={toasts} />

      <div className="mb-8 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-[#4391d1]">Custom Order</p>
          <h1 className="text-3xl font-black text-gray-900">Place order for customer</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-500">
            Create phone, email, walk-in, or special-request orders on behalf of customers using catalog SKUs or manual line items.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]" onClick={loadOrders} type="button">
            <RefreshCw size={16} /> Refresh Orders
          </button>
          <button className="inline-flex items-center gap-2 rounded-xl border border-red-100 bg-white px-4 py-2 text-sm font-black text-red-600 hover:bg-red-50" onClick={resetDraft} type="button">
            <Trash2 size={16} /> Clear Draft
          </button>
        </div>
      </div>

      <div className="mb-8 grid gap-5 md:grid-cols-4">
        <InsightCard title="Draft Items" value={metrics.draftItems} note="Line items in this order" />
        <InsightCard title="Draft Total" value={formatMoney(metrics.draftTotal)} note="After discount, tax, shipping" tone="money" />
        <InsightCard title="Open Orders" value={metrics.openManualOrders} note="Orders still active" tone="good" />
        <InsightCard title="Payment Follow-up" value={metrics.unpaid} note="Manual orders not marked paid" tone={metrics.unpaid ? "warn" : "good"} />
      </div>

      <form className="grid gap-6 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]" onSubmit={createOrder}>
        <div className="space-y-6">
          <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-black text-gray-900">Customer</h2>
                <p className="mt-1 text-xs font-bold uppercase text-gray-400">Search existing account or enter new customer</p>
              </div>
              <UserPlus className="text-[#4391d1]" size={20} />
            </div>

            <label className="relative block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 pl-10 text-sm font-bold outline-none focus:border-[#4391d1]"
                onChange={(event) => setCustomerSearch(event.target.value)}
                placeholder="Search name, email, or phone"
                value={customerSearch}
              />
            </label>

            <div className="mt-4 grid max-h-56 gap-2 overflow-y-auto">
              {loading.customers && <p className="rounded-xl bg-gray-50 p-3 text-sm font-bold text-gray-500">Loading customers...</p>}
              {!loading.customers && customers.slice(0, 8).map((customer) => (
                <button
                  className={`rounded-xl border px-4 py-3 text-left transition ${
                    selectedCustomer?._id === customer._id ? "border-[#4391d1] bg-blue-50" : "border-gray-100 bg-white hover:bg-gray-50"
                  }`}
                  key={customer._id}
                  onClick={() => selectCustomer(customer)}
                  type="button"
                >
                  <div className="font-black text-gray-900">{customer.name}</div>
                  <div className="mt-1 flex flex-wrap gap-2 text-xs font-bold text-gray-500">
                    <span>{customer.email}</span>
                    {customer.phone && <span>{customer.phone}</span>}
                    {customerDiscount(customer) > 0 && <span>{customerDiscount(customer)}% discount</span>}
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-5 grid gap-3 md:grid-cols-2">
              <label>
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">Customer name</span>
                <input className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold" name="customerName" onChange={updateCustomerForm} required value={customerForm.customerName} />
              </label>
              <label>
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">Email</span>
                <input className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold" name="customerEmail" onChange={updateCustomerForm} required type="email" value={customerForm.customerEmail} />
              </label>
              <label className="md:col-span-2">
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">Phone</span>
                <input className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold" name="phone" onChange={updateCustomerForm} value={customerForm.phone} />
              </label>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-black text-gray-900">Shipping Address</h2>
                <p className="mt-1 text-xs font-bold uppercase text-gray-400">Used for fulfillment and invoice records</p>
              </div>
              <MapPin className="text-[#4391d1]" size={20} />
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {[
                ["fullName", "Full name"],
                ["phone", "Phone"],
                ["line1", "Address line 1"],
                ["line2", "Address line 2"],
                ["city", "City"],
                ["state", "State"],
                ["postalCode", "Postal code"],
                ["country", "Country"],
              ].map(([name, label]) => (
                <label className={name === "line1" || name === "line2" ? "md:col-span-2" : ""} key={name}>
                  <span className="mb-2 block text-xs font-black uppercase text-gray-500">{label}</span>
                  <input
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold"
                    name={name}
                    onChange={updateAddress}
                    required={["fullName", "line1", "city", "state", "postalCode"].includes(name)}
                    value={address[name]}
                  />
                </label>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-black text-gray-900">Order Settings</h2>
                <p className="mt-1 text-xs font-bold uppercase text-gray-400">Payment, channel, tax, and internal notes</p>
              </div>
              <ClipboardList className="text-[#4391d1]" size={20} />
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <label>
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">Channel</span>
                <select className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold" name="channel" onChange={updateSettings} value={settings.channel}>
                  <option value="direct">Direct</option>
                  <option value="bobbinusa">Bobbin USA</option>
                  <option value="facebook_marketplace">Facebook market place</option>
                  <option value="ebay">eBay</option>
                  <option value="walmart">Walmart</option>
                </select>
              </label>
              <label>
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">Order status</span>
                <select className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold" name="status" onChange={updateSettings} value={settings.status}>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="processing">Processing</option>
                </select>
              </label>
              <label>
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">Payment status</span>
                <select className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold" name="paymentStatus" onChange={updateSettings} value={settings.paymentStatus}>
                  <option value="paid">Paid</option>
                  <option value="unpaid">Unpaid</option>
                  <option value="failed">Failed</option>
                </select>
              </label>
              <label>
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">Payment provider</span>
                <select className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold" name="paymentProvider" onChange={updateSettings} value={settings.paymentProvider}>
                  <option value="manual">Manual</option>
                  <option value="card">Card</option>
                  <option value="paypal">PayPal</option>
                </select>
              </label>
              <label>
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">Shipping</span>
                <input className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold" min="0" name="shipping" onChange={updateSettings} type="number" value={settings.shipping} />
              </label>
              <label>
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">Tax</span>
                <input className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold" min="0" name="tax" onChange={updateSettings} type="number" value={settings.tax} />
              </label>
              <label className="md:col-span-2">
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">Internal notes</span>
                <textarea className="min-h-24 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold" name="adminNotes" onChange={updateSettings} value={settings.adminNotes} />
              </label>
            </div>
          </section>
        </div>

        <div className="space-y-6">
          <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-black text-gray-900">Add Items</h2>
                <p className="mt-1 text-xs font-bold uppercase text-gray-400">Search catalog or add a manual item</p>
              </div>
              <PackagePlus className="text-[#4391d1]" size={20} />
            </div>

            <label className="relative block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 pl-10 text-sm font-bold outline-none focus:border-[#4391d1]"
                onChange={(event) => setProductSearch(event.target.value)}
                placeholder="Search product name, SKU, brand, or category"
                value={productSearch}
              />
            </label>

            <div className="mt-4 grid max-h-80 gap-3 overflow-y-auto">
              {loading.products && <p className="rounded-xl bg-gray-50 p-3 text-sm font-bold text-gray-500">Loading products...</p>}
              {!loading.products && products.map((product) => (
                <div className="flex flex-col gap-3 rounded-xl border border-gray-100 p-4 sm:flex-row sm:items-center sm:justify-between" key={product._id}>
                  <div className="min-w-0">
                    <p className="truncate font-black text-gray-900">{product.name}</p>
                    <p className="mt-1 text-xs font-bold text-gray-500">{product.sku} / {product.brandLine || "General"} / Stock {product.stock ?? 0}</p>
                    <p className="mt-1 text-xs font-black text-[#4391d1]">{formatMoney(product.price)}</p>
                  </div>
                  <button className="rounded-xl bg-[#4391d1] px-4 py-2 text-sm font-black text-white hover:bg-blue-600" onClick={() => addProduct(product)} type="button">
                    Add
                  </button>
                </div>
              ))}
            </div>

            <form className="mt-5 rounded-xl border border-gray-100 bg-gray-50 p-4" onSubmit={addManualItem}>
              <h3 className="text-sm font-black uppercase text-gray-900">Manual line item</h3>
              <div className="mt-3 grid gap-3 md:grid-cols-4">
                <input className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-bold md:col-span-2" name="name" onChange={updateManualItem} placeholder="Item name" value={manualItem.name} />
                <input className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-bold" name="sku" onChange={updateManualItem} placeholder="SKU" value={manualItem.sku} />
                <input className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-bold" min="1" name="quantity" onChange={updateManualItem} placeholder="Qty" type="number" value={manualItem.quantity} />
                <input className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-bold md:col-span-2" min="0" name="price" onChange={updateManualItem} placeholder="Price" type="number" value={manualItem.price} />
                <button className="rounded-xl bg-gray-900 px-4 py-2 text-sm font-black text-white hover:bg-gray-700 md:col-span-2" type="submit">
                  Add Manual Item
                </button>
              </div>
            </form>
          </section>

          <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-black text-gray-900">Order Draft</h2>
                <p className="mt-1 text-xs font-bold uppercase text-gray-400">Review quantities and pricing before placing</p>
              </div>
              <ShoppingCart className="text-[#4391d1]" size={20} />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[680px] text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-gray-400">
                  <tr>
                    {["Item", "SKU", "Qty", "Price", "Total", ""].map((column) => (
                      <th className="px-3 py-3 font-black" key={column}>{column}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((item, index) => (
                    <tr key={item.key || `${item.sku}-${index}`}>
                      <td className="min-w-56 px-3 py-3 font-black text-gray-900">{item.name}</td>
                      <td className="px-3 py-3 text-xs font-bold text-gray-500">{item.sku || "Manual"}</td>
                      <td className="px-3 py-3">
                        <input className="w-20 rounded-lg border border-gray-200 px-2 py-2 text-sm font-bold" min="1" onChange={(event) => updateItem(index, "quantity", event.target.value)} type="number" value={item.quantity} />
                      </td>
                      <td className="px-3 py-3">
                        <input className="w-28 rounded-lg border border-gray-200 px-2 py-2 text-sm font-bold" min="0" onChange={(event) => updateItem(index, "price", event.target.value)} type="number" value={item.price} />
                      </td>
                      <td className="px-3 py-3 font-black text-gray-900">{formatMoney(lineTotal(item))}</td>
                      <td className="px-3 py-3 text-right">
                        <button className="rounded-lg p-2 text-red-600 hover:bg-red-50" onClick={() => removeItem(index)} type="button">
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!items.length && (
                    <tr>
                      <td className="px-3 py-8 text-center text-sm font-bold text-gray-400" colSpan="6">
                        No items added yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-5 grid gap-2 rounded-xl bg-gray-50 p-4 text-sm font-bold text-gray-700">
              <div className="flex justify-between"><span>Subtotal</span><span>{formatMoney(totals.subtotal)}</span></div>
              <div className="flex justify-between"><span>Customer discount {totals.discountPercent}%</span><span>-{formatMoney(totals.discount)}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span>{formatMoney(totals.shipping)}</span></div>
              <div className="flex justify-between"><span>Tax</span><span>{formatMoney(totals.tax)}</span></div>
              <div className="flex justify-between border-t border-gray-200 pt-3 text-lg font-black text-gray-900"><span>Total</span><span>{formatMoney(totals.total)}</span></div>
            </div>

            <button className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#4391d1] px-5 py-4 text-sm font-black text-white hover:bg-blue-600 disabled:bg-gray-300" disabled={loading.saving} type="submit">
              <CheckCircle2 size={18} /> {loading.saving ? "Placing Order..." : "Place Order for Customer"}
            </button>
          </section>
        </div>
      </form>

      <section className="mt-8 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-gray-100 px-6 py-5 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-black text-gray-900">Recent Custom Orders</h2>
            <p className="mt-1 text-xs font-bold uppercase text-gray-400">Orders available for follow-up</p>
          </div>
          <label className="relative w-full md:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 pl-10 text-sm font-bold outline-none focus:border-[#4391d1]"
              onChange={(event) => setOrderSearch(event.target.value)}
              placeholder="Search recent orders"
              value={orderSearch}
            />
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-400">
              <tr>
                {["Order", "Customer", "Items", "Status", "Payment", "Total", "Action"].map((column) => (
                  <th className="whitespace-nowrap px-6 py-4 font-black" key={column}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading.orders && (
                <tr><td className="px-6 py-8 text-center text-sm font-bold text-gray-400" colSpan="7">Loading orders...</td></tr>
              )}
              {!loading.orders && orderPagination.paginatedItems.map((order) => (
                <tr className="text-sm text-gray-600 hover:bg-blue-50/40" key={order._id}>
                  <td className="px-6 py-4 font-black text-gray-900">{order.orderNumber}</td>
                  <td className="min-w-56 px-6 py-4">
                    <div className="font-bold text-gray-900">{order.user?.name}</div>
                    <div className="text-xs text-gray-400">{order.user?.email}</div>
                  </td>
                  <td className="min-w-64 px-6 py-4 text-sm font-semibold text-gray-600">
                    {(order.items || []).map((item) => `${item.quantity} x ${item.name}`).join(", ")}
                  </td>
                  <td className="px-6 py-4"><StatusPill tone={statusTone(order.status)}>{order.status}</StatusPill></td>
                  <td className="px-6 py-4"><StatusPill tone={paymentTone(order.paymentStatus)}>{order.paymentStatus}</StatusPill></td>
                  <td className="px-6 py-4 font-black text-gray-900">{formatMoney(order.total)}</td>
                  <td className="px-6 py-4">
                    <button className="inline-flex items-center gap-2 rounded-xl bg-[#4391d1] px-4 py-2 text-xs font-black text-white hover:bg-blue-600" onClick={() => navigate(`/dashboard/orders/${order._id}`)} type="button">
                      <Mail size={14} /> Open
                    </button>
                  </td>
                </tr>
              ))}
              {!loading.orders && !orderPagination.paginatedItems.length && (
                <tr><td className="px-6 py-8 text-center text-sm font-bold text-gray-400" colSpan="7">No custom orders found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <PaginationControls pagination={orderPagination} />
      </section>
    </div>
  );
}
